import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { MemberPlusIcon as OldMemberPlusIcon } from "assets/icons";
import { toggleAddToFavorite } from "store/favorites/thunks";
import { isFavorite } from "store/favorites/selectors";
import {
  FavoriteFilled,
  FavoriteOutlined,
  PeopleIcon,
  SurfaceIcon,
  StarIcon,
} from "./svgIcons.jsx";
import OnePageSlider from "reusableComponents/OnePageSlider";
import getPriceForChosenDuration from "helpers/getPriceForChosenDuration";
import getBadgeStatuses from "helpers/getBadgeStatus";
import getPriceForMinimalDuration from "helpers/getPriceForMinDuration";
import getDiscountedPriceForChosenDuration from "helpers/getDiscountPriceForChosenDuration";
import styles from "./styles.module.scss";
import autogenerateSpaceTitle from "helpers/autogenerateSpaceTitle.js";

const SpaceCard = ({
  place,
  themeColor = null,
  externalId = null,
  forMemberPlus = false,
  onHover = () => {},
  onLeave = () => {},
  imageDisplay = "slider", // "slider" | "single"
  forHomePage = false,
}) => {
  const { t } = useTranslation("global");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const filters = useSelector((state) => state.filters);
  const { userType } = useSelector((state) => state.auth);
  const isFavorited = useSelector(isFavorite(place.id));

  const simplifiedId = place.id.replace("CZW-P", "");
  const statuses = getBadgeStatuses(place);
  const badgeUrl = Object.values(statuses)[0]?.badgeUrl || null;

  const price = getPriceForChosenDuration(place, filters.price_type, t);
  const minPrice = getPriceForMinimalDuration(place, t);
  let discountedPrice;

  if (place.last_minute) {
    discountedPrice = getDiscountedPriceForChosenDuration(
      place,
      filters.price_type,
      t
    );
  }

  // Auto-generate space title
  const generatedSpaceTitle = useMemo(() => {
    return autogenerateSpaceTitle(place, t);
  }, [place, t]);

  // Use condition to decide which color to use
  const useBlackIcons = getComputedStyle(document.documentElement)
    .getPropertyValue("--main-color")
    .trim()
    ? true
    : false;

  const iconColor = useBlackIcons ? "#000000" : "#E91E63";

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    try {
      const response = await dispatch(toggleAddToFavorite(place, userType));
      console.log(response.message);
    } catch (error) {
      console.error("Failed to toggle favorite:", error.message);
    }
  };

  const handleClick = (e) => {
    // Prevent navigation if click originated from slider ARROWS
    if (e.target.closest('.custom-arrow') || e.target.closest('.slick-arrow')) {
      return;
    }
    
    // Prevent navigation if click originated from favorite button
    if (e.target.closest(`.${styles.addToFavourite}`)) {
      return;
    }
    
    // Allow navigation for all other clicks (including images)
    forMemberPlus
      ? navigate(
          `/space/${encodeURIComponent(themeColor)}/${place.id}${
            externalId ? `?externalID=${encodeURIComponent(externalId)}` : ""
          }`
        )
      : navigate(`/space/${place.id}`);
  };

  const handleCtaClick = (e) => {
    e.stopPropagation();
    handleClick(e);
  };

  // Create slider images or single image based on imageDisplay prop
  const spaceImagesRenderer = useMemo(
    () =>
      place.images.map((image) => (
        <img
          className={styles.sliderImage}
          style={{ visibility: image.image_url ? "visible" : "hidden" }}
          src={image.image_url}
          alt={""}
          loading="lazy"
          key={image.id}
        />
      )),
    [place.images]
  );

  // Single image display
  const singleImageRenderer = useMemo(() => {
    const firstImage = place.images[0];
    if (!firstImage) return null;

    return (
      <img
        className={styles.singleImage}
        style={{ visibility: firstImage.image_url ? "visible" : "hidden" }}
        src={firstImage.image_url}
        alt={""}
        loading="lazy"
      />
    );
  }, [place.images]);

  const getSpecialStyles = (place) => {
    let specialBorderColor;
    let hasTriangle = false;
    let hasThickBorder = false;

    if (place.last_minute) {
      specialBorderColor = "rgba(955, 19, 90, 0.9)";
      hasTriangle = true;
      hasThickBorder = false;
    } 
    else if (statuses.zoom && !forHomePage) {
      specialBorderColor = "#e5b53a";
      hasTriangle = true;
      hasThickBorder = true;
    }
    else if (statuses.color && !forHomePage) {
      specialBorderColor = "#72c8da";
      hasTriangle = true;
      hasThickBorder = true;
    }  else if (statuses.favorite  && !forHomePage) {
          specialBorderColor = "#d51b81";
          hasTriangle = true;
          hasThickBorder = true;
    } else if (statuses.highlight  && !forHomePage) {
          specialBorderColor = "#89a335";
          hasTriangle = true;
          hasThickBorder = true;
    }
     else {
      specialBorderColor = null;
      hasTriangle = false;
      hasThickBorder = false;
    }

    // On homepage, only show triangle for last_minute
    if (forHomePage && place.last_minute) {
      hasTriangle = true;
      hasThickBorder = true;
    } else if (forHomePage) {
      hasTriangle = false;
      hasThickBorder = false;
    }

    return { specialBorderColor, hasTriangle, hasThickBorder };
  };

  const { specialBorderColor, hasTriangle, hasThickBorder } = getSpecialStyles(place);

  const getTriangleLabel = () => {
    // Last Minute / First Page
    if (place.last_minute) return t("spaceCard.lastMinute");
    
    // Zoom Sur
    if (!forHomePage && statuses.zoom) return t("spaceCard.zoom") + " 🔍";
    
    // Mise en avant (Featured)
    if (!forHomePage && statuses.color) return t("spaceCard.featured") + " ☆";    
    
    // Coup de Coeur (Favourite)
    if (!forHomePage && statuses.favorite) return  t("spaceCard.favourite") + " ♡";
    
    // Highlight (empty string case)
    if (!forHomePage && statuses.highlight) return t("spaceCard.highlight") + " 🎖";
    
    return "Last Minute";
  };

  return (
    <div
      className={styles.spaceCard}
      key={place.uuid}
      onMouseEnter={() => onHover(place)}
      onMouseLeave={onLeave}
      style={{
        borderColor: specialBorderColor || undefined,
        borderWidth: hasThickBorder ? "2px" : "1px",
      }}
      onClick={handleClick}
    >
      {/* Left side - Image section */}
      <div className={styles.imageSection}>
        {imageDisplay === "slider" ? (
          <OnePageSlider arrowStyle="square" arrowLeft="10px" arrowRight="10px">
            {spaceImagesRenderer}
          </OnePageSlider>
        ) : (
          singleImageRenderer
        )}

        {place.partner && (
          <div className={styles.partner}>
            <img src={OldMemberPlusIcon} alt="memberplus partner" />
          </div>
        )}

        {/* Top right badges container */}
        <div className={styles.topRightBadges}>
          {/* Status badge - smaller size */}
          {badgeUrl && (
            <div
              className={styles.statusBadge}
              style={{ backgroundImage: `url(${badgeUrl})` }}
            ></div>
          )}

          {/* Favorite button - don't show on homepage */}
          {userType !== "provider" && !forHomePage && (
            <button
              className={styles.addToFavourite}
              onClick={handleFavoriteClick}
              aria-label={
                isFavorited ? t("space.removeFromFavorites") : t("space.addToFavorites")
              }
            >
              {isFavorited ? (
                <FavoriteFilled width={20} height={20} color={iconColor} />
              ) : (
                <FavoriteOutlined width={20} height={20} color={iconColor} />
              )}
            </button>
          )}
        </div>

        {hasTriangle && (
          <div
            className={styles.cornerRectangle}
            style={{
              backgroundColor: specialBorderColor || "#72ccdf",
            }}
          >
            {getTriangleLabel()}
          </div>
        )}
      </div>

      {/* Right side - Content section */}
      <div className={styles.contentSection}>
        {/* Top left section with CTA button and info below it */}
        <div className={styles.topLeftSection}>
          {/* CTA Button */}
          <button className={styles.ctaButton} onClick={handleCtaClick}>
            {place.instant_booking
              ? t("space.instantReservation")
              : t("space.discoverSpace")}
          </button>

          {/* Space type and address below the CTA button */}
          <div className={styles.headerInfo}>
            <p className={styles.spaceType}>
              {place.placeUsage?.name} - {simplifiedId}
            </p>
            <p className={styles.spaceAddress}>
              {place.address?.postal_code} {place.address?.city}
            </p>
          </div>
        </div>

        {/* Space name */}
        <h3 className={styles.spaceName}>{generatedSpaceTitle}</h3>

        {/* Space specs */}
        <div className={styles.spaceSpecs}>
          <div className={styles.specItem}>
            <PeopleIcon width={16} height={16} color={iconColor} />
            <span>{place.max_capacity > 0 && place.max_capacity}</span>
          </div>
          <div className={styles.specItem}>
            <SurfaceIcon width={16} height={16} color={iconColor} />
            <span>{place.area}</span>
          </div>
          
          {/* Rating if available - placed inline with specs */}
          {!!place.note_avg && (
            <div className={`${styles.specItem} ${styles.ratingInline}`}>
              <StarIcon width={14} height={14} />
              <span className={styles.ratingValue}>{place.note_avg}</span>
              <span className={styles.ratingText}>/10</span>
            </div>
          )}
        </div>

        {/* Bottom section with price */}
        <div className={styles.bottomSection}>
          <div className={styles.priceInfo}>
            <span className={styles.priceFrom}>{`${t("space.from")}  `}</span>
            <span
              className={styles.priceValue}
              style={{
                textDecoration: place.last_minute ? "line-through" : "none",
              }}
            >
              {minPrice}
            </span>
            {place.last_minute && (
              <span
                className={styles.discountedPrice}
                style={{ color: `${specialBorderColor}`, fontWeight: "600" }}
              >
                {" "}
                {discountedPrice}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpaceCard;